import type { GridPoint } from '../../core/types';
import type { Footprint } from './definitions';

/**
 * Footprint geometry on the existing tile grid.
 *
 * A building's `origin` is its tile with the smallest grid x and y (the TOP
 * corner of its diamond on screen). It covers origin.x .. origin.x+width-1 by
 * origin.y .. origin.y+height-1.
 */
export function footprintTiles(origin: GridPoint, fp: Footprint): GridPoint[] {
  const out: GridPoint[] = [];
  for (let dy = 0; dy < fp.height; dy++) for (let dx = 0; dx < fp.width; dx++) out.push({ x: origin.x + dx, y: origin.y + dy });
  return out;
}

/** Origin that places the footprint centred on the cursor tile (biased to the top for even sizes). */
export function originForCursor(cursor: GridPoint, fp: Footprint): GridPoint {
  return { x: cursor.x - Math.floor((fp.width - 1) / 2), y: cursor.y - Math.floor((fp.height - 1) / 2) };
}

/** Fractional grid centre of the footprint (where the sprite is anchored). */
export function footprintCenter(origin: GridPoint, fp: Footprint): { x: number; y: number } {
  return { x: origin.x + (fp.width - 1) / 2, y: origin.y + (fp.height - 1) / 2 };
}

/** The footprint tile nearest the viewer — used for draw order. */
export function frontTile(origin: GridPoint, fp: Footprint): GridPoint {
  return { x: origin.x + fp.width - 1, y: origin.y + fp.height - 1 };
}

/** Grid-space outline corners (top, right, bottom, left) of the whole footprint, as fractional grid points. */
export function footprintOutline(origin: GridPoint, fp: Footprint): [GridPoint, GridPoint, GridPoint, GridPoint] {
  const x0 = origin.x - 0.5;
  const y0 = origin.y - 0.5;
  const x1 = origin.x + fp.width - 0.5;
  const y1 = origin.y + fp.height - 0.5;
  return [
    { x: x0, y: y0 },
    { x: x1, y: y0 },
    { x: x1, y: y1 },
    { x: x0, y: y1 },
  ];
}

/** Chebyshev distance from a tile to the footprint rectangle (0 = inside). */
export function distanceToFootprint(t: GridPoint, origin: GridPoint, fp: Footprint): number {
  const dx = Math.max(origin.x - t.x, 0, t.x - (origin.x + fp.width - 1));
  const dy = Math.max(origin.y - t.y, 0, t.y - (origin.y + fp.height - 1));
  return Math.max(dx, dy);
}
